<template>
  <div class="CheckBoxInput"
       :class="{
         [`CheckBoxInput--${size}`]: !!size,
         'CheckBoxInput--disabled': disabled,
         'CheckBoxInput--readonly': readonly,
         'CheckBoxInput--highlight': isHighlighted,
         'CheckBoxInput--invalid': isInvalid,
         'CheckBoxInput--with_icon': $slots.icon,
       }">
    <label :for="inputId" class="CheckBoxInput__label">
      <input :id="inputId"
             :value="value || modelValue"
             :checked="!!modelValue"
             type="checkbox"
             :name="name"
             :disabled="disabled || readonly"
             :tabindex="tabindex"
             class="CheckBoxInput__input"
             data-test-id="input"
             @change="onChange"
      />
      <span v-if="$slots.icon" class="CheckBoxInput__icon">
        <slot name="icon" />
      </span>
      <span v-else class="CheckBoxInput__checkbox">
        <Icon name="checkmark_4" />
      </span>
      <span v-if="label || $slots.default" class="CheckBoxInput__text" data-test-id="label">
        <template v-if="label">{{ label }}</template>
        <slot v-else-if="$slots.default" />
      </span>
    </label>
    <div v-if="errors.length > 0" class="CheckBoxInput__error" data-test-id="errors">{{ errors[0].$message }}</div>
  </div>
</template>

<script setup lang="ts">
  // TODO: Покрасить красным ошибочный
  // TODO: Добавить дженерик для типа modelValue
  import { computed } from 'vue';
  import { UiSize } from '../../../constants';
  import Icon from '../../Icon.vue';
  import { InputErrorObject } from './types';

  const props = withDefaults(defineProps<{
    modelValue?: boolean | string | number;
    value?: boolean | string | number;
    name: string;
    label?: string;
    invalid?: boolean;
    errors?: InputErrorObject[];
    disabled?: boolean;
    readonly?: boolean;
    highlight?: boolean;
    size?: UiSize;
    tabindex?: number;
  }>(), {
    size: UiSize.MD,
    errors: () => [],
    tabindex: 0,
  });

  const emit = defineEmits<{
    (e: 'update:model-value', modelValue: boolean | string | number): void;
  }>();

  const inputId = computed(() => [props.name, props.value].filter((v) => !!v).join('::'));
  const isHighlighted = computed(() => Boolean(props.highlight && !props.modelValue));
  const isInvalid = computed(() => Boolean(props.invalid || props.errors.length));

  const onChange = (event: Event) => {
    if (props.disabled || props.readonly) {
      return;
    }

    const { value, checked } = (event.target as HTMLInputElement);

    emit('update:model-value', props.value ? checked ? value : null : checked);
  };
</script>

<style lang="scss" scoped>
  @import '../../../assets/scss/_variables.scss';
  @import '../../../assets/scss/_mixins.scss';

  .CheckBoxInput {
    @include transition($transition-function, $transition-duration, opacity);

    display: flex;
    flex-direction: column;
    width: 100%;

    &--highlight {
      // ...
    }

    &--disabled {
      opacity: 0.5;
      pointer-events: none;
    }

    &__label {
      position: relative;
      display: flex;
      align-items: center;
      gap: 5px;
      line-height: 28px;
      white-space: nowrap;
    }

    &__input {
      @include visually-hidden();
    }

    &--with_icon {
      gap: 8px;
    }

    &__icon {
      width: 22px;
      height: 22px;
    }

    &__checkbox {
      @include transition($transition-function, $transition-duration, border-color, background-color);

      display: flex;
      padding: 1px;
      background-color: $color-white;
      border: 1px solid #a5adb6;
      border-radius: 2px;
      color: $color-second;
      flex-shrink: 0;

      .svg-icon {
        @include transition($transition-function, $transition-duration, color);

        color: transparent;
      }
    }

    &--md &__checkbox {
      width: 14px;
      height: 14px;
    }

    &--lg &__checkbox {
      width: 20px;
      height: 20px;
    }

    &--highlight &__checkbox {
      background-color: $color-light-yellow;
    }

    &__input:checked + &__checkbox {
      border-color: currentColor;
      background-color: currentColor;

      .svg-icon {
        color: $color-white;
      }
    }

    &__text {
      // ...
    }

    &__error {
      margin-top: 5px;
      color: $color-red-100;
      font-size: 11px;
    }
  }
</style>
